import React, { useEffect, useState } from 'react';
import './CartModal.css';
import { useTranslation } from 'react-i18next';
import {useNavigate} from "react-router-dom";

export default function CartModal({
                                      visible,
                                      onClose,
                                      userData,  // { token, firstname, ... } або null
                                  }) {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language === 'ua' ? 'ua' : 'en';

    const [cartItems, setCartItems] = useState([]);
    const [uniqueItemsCount, setUniqueItemsCount] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    // const navigate = useNavigate();

    const LAMBDA_URL = 'https://thskp6o2fa52da7h27qkh6ndw40hfban.lambda-url.eu-west-2.on.aws';

    useEffect(() => {
        if (visible) {
            loadCart();
        }
    }, [visible]);

    // ==========================
    // Завантаження кошика
    // ==========================
    const loadCart = async () => {
        setIsLoading(true);
        setCartItems([]);
        setUniqueItemsCount(0);
        setTotalAmount(0);

        if (userData?.token) {
            try {
                const res = await fetch(`${LAMBDA_URL}/cart`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${userData.token}`
                    }
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.message || 'Error fetching cart');

                const items = data.items || [];
                setCartItems(items);
                setUniqueItemsCount(data.uniqueItemsCount || items.length);
                setTotalAmount(calcTotalAmount(items));
            } catch (err) {
                console.error('Could not load cart:', err);
                // При помилці - вважаємо кошик порожнім
            } finally {
                setIsLoading(false);
            }
        } else {
            // Неавторизований => порожньо або cookie
            setIsLoading(false);
            setCartItems([]);
            setUniqueItemsCount(0);
            setTotalAmount(0);
        }
    };

    // Функція для знаходження ціни на основі обраних параметрів
    const findPriceByParams = (prices, priceParams) => {
        if (!prices || !Array.isArray(prices) || prices.length === 0) {
            return null;
        }
        
        // Якщо немає параметрів, повертаємо першу ціну
        if (!priceParams || Object.keys(priceParams).length === 0) {
            return prices[0];
        }
        
        // Шукаємо ціну, яка відповідає всім параметрам
        for (const price of prices) {
            let match = true;
            for (const [key, value] of Object.entries(priceParams)) {
                if (price[key] !== value) {
                    match = false;
                    break;
                }
            }
            if (match) {
                return price;
            }
        }
        
        // Якщо не знайдено, повертаємо першу ціну
        return prices[0];
    };

    // Підрахунок загальної суми
    const calcTotalAmount = (items) => {
        let sum = 0;
        items.forEach(item => {
            // Отримуємо ціну з chosenPrice або знаходимо в prices
            let priceVal = 0;
            
            if (item?.chosenPrice?.[currentLang]) {
                // Старий формат - використовуємо chosenPrice
                priceVal = Number(item.chosenPrice[currentLang] || 0);
            } else if (item?.prices && Array.isArray(item.prices)) {
                // Новий формат - шукаємо відповідну ціну в масиві prices
                const priceObj = findPriceByParams(item.prices, item.priceParams);
                priceVal = Number(priceObj?.price?.[currentLang] || 0);
            }
            
            sum += priceVal * (item.count || 1);
        });
        return sum;
    };

    // ==========================
    // Локальне оновлення item (щоб не "перезавантажувати" всю корзину)
    // ==========================
    const updateItemInState = (updatedItem) => {
        // Якщо count=0 => видаляємо його з локального стейту
        if (updatedItem.count === 0) {
            const newItems = cartItems.filter(it => !isSameItem(it, updatedItem));
            setCartItems(newItems);
            setUniqueItemsCount(newItems.length);
            setTotalAmount(calcTotalAmount(newItems));
            return;
        }

        // Інакше: або оновлюємо існуючий, або додаємо
        let found = false;
        const newItems = cartItems.map(it => {
            if (isSameItem(it, updatedItem)) {
                found = true;
                return {
                    ...it,
                    count: updatedItem.count,
                    chosenPrice: updatedItem.chosenPrice,
                    prices: updatedItem.prices,
                    title: updatedItem.title,
                    mainImage: updatedItem.mainImage,
                    chosenParamsLabels: updatedItem.chosenParamsLabels
                };
            }
            return it;
        });

        if (!found) {
            newItems.push(updatedItem);
        }

        setCartItems(newItems);
        setUniqueItemsCount(newItems.length);
        setTotalAmount(calcTotalAmount(newItems));
    };

    const isSameItem = (a, b) => {
        return (
            a.productId === b.productId &&
            JSON.stringify(a.priceParams) === JSON.stringify(b.priceParams)
        );
    };

    // ==========================
    // Increment / Decrement
    // ==========================
    const incrementItem = async (productId, priceParams) => {
        if (!userData?.token) {
            alert(t('cart.unloggedIncrement'));
            return;
        }
        try {
            const res = await fetch(`${LAMBDA_URL}/increment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${userData.token}`
                },
                body: JSON.stringify({ productId, priceParams })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Error incrementing item');

            updateItemInState({
                ...data,
                count: data.count || 1
            });
        } catch (err) {
            console.error('Increment error:', err);
        }
    };

    const decrementItem = async (productId, priceParams) => {
        if (!userData?.token) {
            alert(t('cart.unloggedDecrement'));
            return;
        }
        try {
            const res = await fetch(`${LAMBDA_URL}/decrement`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${userData.token}`
                },
                body: JSON.stringify({ productId, priceParams })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Error decrementing item');

            if (data.message?.includes('removed')) {
                // Товар видалено (count = 0)
                updateItemInState({ productId, priceParams, count: 0 });
            } else {
                updateItemInState({
                    ...data,
                    count: data.count || 1
                });
            }
        } catch (err) {
            console.error('Decrement error:', err);
        }
    };

    // ==========================
    // Видалення (натискання "delete_outline")
    // ==========================
    const removeItem = async (item) => {
        if (!userData?.token) {
            alert(t('cart.unloggedDecrement')); // або t('cart.unloggedRemove')
            return;
        }
        try {
            const res = await fetch(`${LAMBDA_URL}/remove`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${userData.token}`
                },
                body: JSON.stringify({
                    productId: item.productId,
                    priceParams: item.priceParams
                })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Error removing item');

            // Якщо успіх => локально видаляємо товар
            updateItemInState({ productId: item.productId, priceParams: item.priceParams, count: 0 });
        } catch (err) {
            console.error('Remove item error:', err);
            alert('Failed to remove item: ' + err.message);
        }
    };

    // Закриття по кліку поза модалкою
    const handleBackdropClick = (e) => {
        if (e.target.classList.contains('cart-modal-backdrop')) {
            onClose();
        }
    };

    if (!visible) return null;

    const cartTitle = t('cart.title', { count: uniqueItemsCount });

    const navigateToCheckout = () => {
        // navigate('/checkout');
        window.location.href = '/checkout';
    };
    return (
        <div className="cart-modal-backdrop" onClick={handleBackdropClick}>
            <div className="cart-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="cart-modal-close" onClick={onClose}>×</button>

                {isLoading ? (
                    <div className="cart-modal-loading">
                        <p>{t('cart.loading')}</p>
                    </div>
                ) : (
                    <>
                        {cartItems.length === 0 ? (
                            <div className="cart-modal-empty">
                                <h2>{cartTitle}</h2>
                                <p>{t('cart.empty')}</p>
                            </div>
                        ) : (
                            <>
                                <h2>{cartTitle}</h2>
                                <div className="cart-items-list">
                                    {cartItems.map((item, idx) => {
                                        const productName = item?.title?.[currentLang] || '';
                                        
                                        // Визначаємо ціну товару з урахуванням нової структури
                                        let priceVal = 0;
                                        if (item?.chosenPrice?.[currentLang]) {
                                            // Старий формат - використовуємо chosenPrice
                                            priceVal = Number(item.chosenPrice[currentLang] || 0);
                                        } else if (item?.prices && Array.isArray(item.prices)) {
                                            // Новий формат - шукаємо відповідну ціну в масиві prices
                                            const priceObj = findPriceByParams(item.prices, item.priceParams);
                                            priceVal = Number(priceObj?.price?.[currentLang] || 0);
                                        }
                                        
                                        const count = item.count || 1;
                                        const totalForItem = (priceVal * count).toFixed(2);

                                        let paramsString = '';
                                        if (item.chosenParamsLabels) {
                                            const arr = Object.values(item.chosenParamsLabels).map(obj => {
                                                return obj.labels?.[currentLang] || obj.value;
                                            });
                                            paramsString = arr.join(', ');
                                        }

                                        // Готовий URL зображення
                                        const mainImgUrl = item.mainImage
                                            ? item.mainImage
                                            : '/fallback.png';

                                        return (
                                            <div key={idx} className="cart-item-row">
                                                {/* Зображення */}
                                                <img
                                                    src={mainImgUrl}
                                                    alt={productName}
                                                    className="cart-item-image"
                                                />
                                                <div className="cart-item-details">
                                                    <h4>{productName}</h4>
                                                    {paramsString && (
                                                        <p className="cart-item-params">{paramsString}</p>
                                                    )}
                                                    <p className="cart-item-qty">
                                                        {t('cart.quantity')}: {count}
                                                    </p>
                                                    <p className="cart-item-price">
                                                        {t('cart.price')}: {priceVal.toFixed(2)} {i18n.language === 'ua' ? 'UAH' : 'USD'}
                                                    </p>
                                                    <div className="cart-item-actions">
                                                        <button
                                                            onClick={() => decrementItem(item.productId, item.priceParams)}
                                                            disabled={count <= 1}
                                                        >
                                                            −
                                                        </button>
                                                        <span>{count}</span>
                                                        <button
                                                            onClick={() => incrementItem(item.productId, item.priceParams)}
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="cart-item-total">
                                                    {totalForItem} {i18n.language === 'ua' ? 'UAH' : 'USD'}
                                                </div>
                                                <button
                                                    className="cart-item-remove"
                                                    onClick={() => removeItem(item)}
                                                >
                                                    <span className="material-icons-outlined">delete_outline</span>
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="cart-summary">
                                    <h3>
                                        {t('cart.total')}: {totalAmount.toFixed(2)} {i18n.language === 'ua' ? 'UAH' : 'USD'}
                                    </h3>
                                </div>

                                <button className="cart-checkout-btn" onClick={() => navigateToCheckout()}>
                                    {t('cart.checkout')}
                                </button>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
